import { useMemo } from 'react';
import { Box, Theme, Typography, useMediaQuery } from '@mui/material';
import { useTranslation } from 'react-i18next';

import { PLAN_CODE_YEARLY, PLAN_CODE_MONTHLY } from 'constants/payment';
import { COUPON } from 'constants/index';
import { useLocalStorageState } from 'hooks';
import { CouopnDescription, Coupon, CouponDiscountBy, CouponType } from 'types';
import { getCouponDescriptionModel, isCouponValidForPlan } from 'utils';

import styles from './LeftPromocodeContainer.module.scss';

export function LeftPromocodeContainer() {
  const { t } = useTranslation('auth');
  const isMobile = useMediaQuery((theme: Theme) => theme.breakpoints.down('sm'));
  const [coupon] = useLocalStorageState<Coupon | null>(COUPON, null);

  const topDescriptionFlatDurationString: string = useMemo(() => {
    if (!coupon) {
      return '';
    }

    if (coupon.type === CouponType.OneTime) {
      return t('oneTime');
    }

    if (coupon.type === CouponType.Forever) {
      return t('forever');
    }

    if (isCouponValidForPlan(coupon, PLAN_CODE_MONTHLY)) {
      return t('forMonths', { count: coupon.duration });
    }

    if (isCouponValidForPlan(coupon, PLAN_CODE_YEARLY)) {
      return t('forYears', { count: coupon.duration });
    }

    return '';
  }, [coupon, t]);

  const couopnDescription: CouopnDescription = useMemo(() => {
    return coupon ? getCouponDescriptionModel(coupon) : ({} as CouopnDescription);
  }, [coupon]);

  return (
    <Box className={styles.container}>
      <div className={styles.topContainer}>
        <div className={styles.circle1}></div>
        <div className={styles.circle2}></div>

        {coupon?.discountBy === CouponDiscountBy.Percentage && (
          <div className={styles.topDescriptionPercentage}>
            <Typography variant='body1' component='span' className={styles.percentValue}>
              {coupon.discountValue}
            </Typography>

            <Typography variant='body1' component='span' className={styles.percent}>
              %
            </Typography>

            <Typography variant='body1' component='span' className={styles.off}>
              OFF
            </Typography>
          </div>
        )}

        {coupon?.discountBy === CouponDiscountBy.Flat && (
          <div className={styles.topDescriptionFlat}>
            <Typography variant='body1' component='span' className={styles.flatValue}>
              ${coupon.discountValue}
            </Typography>

            <Typography variant='body1' component='span' className={styles.by}>
              {isCouponValidForPlan(coupon, PLAN_CODE_MONTHLY) ? t('perMonth') : t('perYear')}
            </Typography>

            <Typography variant='body1' component='span' className={styles.duration}>
              {topDescriptionFlatDurationString}
            </Typography>
          </div>
        )}
      </div>

      <div className={styles.bottomContainer}>
        <Typography variant='h3' component='h3'>
          {t('yourPromoCode')}
        </Typography>

        <div className={styles.promoCodeBorder}>
          <div className={styles.promoCode}>{coupon?.code}</div>
        </div>

        <Typography variant={!isMobile ? 'h3' : 'body4'} component={!isMobile ? 'h3' : 'span'}>
          {couopnDescription.PromoTitle}
        </Typography>

        <Typography variant={!isMobile ? 'body2' : 'body1'} component='span'>
          {couopnDescription.PromoSubTitle}
        </Typography>
      </div>
    </Box>
  );
}
